import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCourses, getTrip, patchRound } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, MagnifyingGlass, MapPin, Check, Lock, LockOpen } from '@phosphor-icons/react';
import { roundHasScores } from '../lib/formatHelpers';
import { setRoundUnlockToken } from '../lib/ownerAuth';
import { getTripAccess } from '../lib/tripAccess';
import { RoleGated } from '../components/RoleGated';
import OwnerPasswordDialog from '../components/OwnerPasswordDialog';

export default function CoursePicker() {
  const { code, roundId } = useParams();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTee, setSelectedTee] = useState(null);
  const [teeDialogOpen, setTeeDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customPar, setCustomPar] = useState('72');
  const [customCR, setCustomCR] = useState('72');
  const [customSlope, setCustomSlope] = useState('113');
  const [round, setRound] = useState(null);
  const [ownerUnlocked, setOwnerUnlocked] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);

  useEffect(() => {
    getCourses().then(r => { setCourses(r.data); setLoading(false); }).catch(() => setLoading(false));
    // Fetch trip to check if round has scores
    getTrip(code).then(r => {
      const rd = r.data?.rounds?.find(r => r.id === roundId);
      if (rd) setRound(rd);
    }).catch(() => {});
  }, [code, roundId]);

  const hasScores = round ? roundHasScores(round) : false;
  const scoredLocked = hasScores && !ownerUnlocked;
  const access = getTripAccess(code);
  const role = access?.role || (access?.legacy ? 'organiser' : null);
  const isOrganiser = role === 'organiser';
  const locked = scoredLocked || !isOrganiser;

  const handleSearch = useCallback((val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 150);
  }, []);

  const MAX_RENDERED = 100;

  const filtered = useMemo(() => {
    if (!debouncedSearch.trim()) return courses;
    const q = debouncedSearch.trim().toLowerCase();
    return courses.filter(c => c.name.toLowerCase().includes(q) || (c.region || '').toLowerCase().includes(q));
  }, [courses, debouncedSearch]);

  const displayed = filtered.slice(0, MAX_RENDERED);
  const hiddenCount = filtered.length - displayed.length;

  const handleSelectCourse = (course) => {
    const teeList = course.tees || [];
    if (teeList.length > 1) {
      setSelectedCourse(course);
      setSelectedTee(teeList[0]);
      setTeeDialogOpen(true);
    } else if (teeList.length === 1) {
      confirmCourse(course, teeList[0]);
    } else {
      confirmCourse(course, null);
    }
  };

  const confirmCourse = async (course, tee) => {
    setSaving(true);
    const teeList = course.tees || [];
    // Build snapshot tees for per-player tee support in effectiveCourseForPlayer
    const snapshotTees = teeList.map(t => ({
      name: t.name,
      par: t.hole_pars ? t.hole_pars.reduce((a, b) => a + b, 0) : course.par,
      course_rating: t.course_rating, slope_rating: t.slope_rating,
      ...(t.hole_pars ? { hole_pars: t.hole_pars } : {}),
      ...(t.stroke_indexes ? { stroke_indexes: t.stroke_indexes } : {}),
    }));
    const chosenHp = tee?.hole_pars || teeList[0]?.hole_pars || course.hole_pars;
    const chosenSi = tee?.stroke_indexes || teeList[0]?.stroke_indexes || course.stroke_indexes;
    const chosenPar = chosenHp ? chosenHp.reduce((a, b) => a + b, 0) : course.par;
    const courseData = {
      id: course.id, name: course.name, region: course.region,
      par: chosenPar,
      course_rating: tee?.course_rating ?? teeList[0]?.course_rating ?? course.course_rating,
      slope_rating: tee?.slope_rating ?? teeList[0]?.slope_rating ?? course.slope_rating,
      tee_name: tee?.name || null,
      tees: snapshotTees,
      hole_pars: chosenHp,
      stroke_indexes: chosenSi,
      custom: course.custom || false,
    };
    try {
      await patchRound(code, roundId, {
        course: courseData,
        ...(chosenHp ? { hole_pars: chosenHp } : {}),
        ...(chosenSi ? { stroke_indexes: chosenSi } : {}),
      });
      navigate(`/trip/${code}/round/${roundId}/setup`);
    } catch {} finally { setSaving(false); }
  };

  const handleCustom = async () => {
    if (!customName.trim()) return;
    const course = {
      id: 'custom-' + Date.now(),
      name: customName.trim(),
      par: parseInt(customPar) || 72,
      course_rating: parseFloat(customCR) || 72,
      slope_rating: parseInt(customSlope) || 113,
      custom: true,
    };
    confirmCourse(course, null);
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="course-picker">
      {/* Sticky header with glass */}
      <div className="sticky top-0 z-20 glass border-b border-[#E1E6E4]">
        <div className="px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/trip/${code}/round/${roundId}/setup`)} className="p-1" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-[#1D242B]" weight="bold" />
          </button>
          <h1 className="text-lg text-[#1D242B] font-bold flex-1">Select Course</h1>
        </div>
        <div className="px-4 pb-3">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#B8C7C0]" />
            <Input data-testid="course-search" placeholder="Search courses or clubs..." value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-11 h-12 rounded-xl bg-[#F4F6F5] border-[#E1E6E4] text-base" autoFocus />
          </div>
        </div>
      </div>

      {/* Course list */}
      <div className="px-4 py-3 max-w-lg mx-auto">
        {!isOrganiser && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-[#F4F6F5] text-[#B8C7C0] border border-[#E1E6E4] mb-3" data-testid="course-lock-banner">
            <Lock className="w-4 h-4 flex-shrink-0" weight="bold" />
            <span>View only — organiser access required</span>
          </div>
        )}
        {isOrganiser && hasScores && (
          <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold mb-3 ${
            scoredLocked ? 'bg-[#FFF3E0] text-[#E59000] border border-[#FFE0B2]' : 'bg-[#E8F5E9] text-[#2E7D32] border border-[#C8E6C9]'
          }`} data-testid="course-lock-banner">
            {scoredLocked ? (
              <>
                <Lock className="w-4 h-4 flex-shrink-0" weight="bold" />
                <span className="flex-1">Master password required — round has scores</span>
                <button onClick={() => setOwnerDialogOpen(true)}
                  className="ml-auto px-3 py-1 text-xs font-bold bg-[#E59000] text-white rounded-lg hover:bg-[#D48400] transition-colors whitespace-nowrap"
                  data-testid="unlock-course-btn">Unlock (owner)</button>
              </>
            ) : (
              <><LockOpen className="w-4 h-4 flex-shrink-0" weight="bold" /><span>Unlocked — course changes allowed</span></>
            )}
          </div>
        )}
        <OwnerPasswordDialog open={ownerDialogOpen} onOpenChange={setOwnerDialogOpen}
          onSuccess={(token) => { setOwnerDialogOpen(false); setRoundUnlockToken(code, roundId, token); setOwnerUnlocked(true); }}
          description="This round has scores recorded. Enter the master password to unlock course changes."
          tripCode={code} />
        {loading ? (
          <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-white rounded-xl animate-pulse border border-[#E1E6E4]" />)}</div>
        ) : (
          <>
            <div className="space-y-2 ft-scroll">
              {displayed.map(c => (
                <Card key={c.id} className={`bg-white border-[#E1E6E4] rounded-xl transition-shadow ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-sm'}`}
                  onClick={() => !locked && handleSelectCourse(c)} data-testid={`course-${c.id}`}>
                  <div className="flex items-center min-h-[56px] px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1D242B] text-sm truncate">{c.name}</p>
                      {c.region && <p className="text-xs text-[#455561] flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{c.region}</p>}
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <p className="text-xs text-[#455561]">Par {c.par}</p>
                      <p className="text-[10px] text-[#B8C7C0]">
                        {(c.tees || []).length > 1
                          ? `${c.tees.length} tees`
                          : `CR ${c.tees?.[0]?.course_rating ?? c.course_rating ?? '—'} · S ${c.tees?.[0]?.slope_rating ?? c.slope_rating ?? '—'}`}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
              {hiddenCount > 0 && (
                <p className="text-center text-xs text-[#B8C7C0] py-3" data-testid="course-more-hint">
                  {hiddenCount} more — refine your search
                </p>
              )}
              {filtered.length === 0 && (
                <div className="text-center py-12">
                  <MagnifyingGlass className="w-10 h-10 text-[#B8C7C0] mx-auto mb-3" />
                  <p className="text-[#455561] text-sm">No courses found</p>
                </div>
              )}
            </div>
            {/* Custom course CTA */}
            <div className="sticky bottom-0 pt-4 pb-6">
              <Button onClick={() => !locked && setCustomOpen(true)} variant="outline" disabled={locked}
                className="w-full h-14 rounded-xl border-[#E1E6E4] bg-white text-[#1D242B] text-base font-semibold disabled:opacity-40" data-testid="add-custom-course-btn">
                Add Custom Course
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Tee selection dialog */}
      <Dialog open={teeDialogOpen} onOpenChange={setTeeDialogOpen}>
        <DialogContent className="sm:max-w-sm rounded-2xl">
          <DialogHeader><DialogTitle>Select Tee — {selectedCourse?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2 max-h-60 overflow-y-auto ft-scroll">
            {(selectedCourse?.tees || []).map((tee, i) => (
              <Card key={i} onClick={() => setSelectedTee(tee)}
                className={`p-3 cursor-pointer transition-all rounded-xl border-2 ${selectedTee?.name === tee.name ? 'border-[#1B4332] bg-[#E8E5DB]' : 'border-[#E1E6E4] hover:border-[#B8C7C0]'}`}
                data-testid={`tee-option-${i}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-[#1D242B]">{tee.name}</p>
                    <p className="text-xs text-[#455561]">{tee.course_rating != null ? `CR ${tee.course_rating}` : ''}{tee.course_rating != null && tee.slope_rating != null ? ' · ' : ''}{tee.slope_rating != null ? `S ${tee.slope_rating}` : ''}</p>
                  </div>
                  {selectedTee?.name === tee.name && <Check className="w-5 h-5 text-[#1B4332]" weight="bold" />}
                </div>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => { setTeeDialogOpen(false); confirmCourse(selectedCourse, selectedTee); }}
              disabled={saving} className="w-full bg-[#1B4332] hover:bg-[#163B2B] text-white h-14 rounded-xl text-base font-semibold" data-testid="confirm-tee-btn">
              {saving ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom course dialog */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="sm:max-w-sm rounded-2xl">
          <DialogHeader><DialogTitle>Custom Course</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input data-testid="custom-course-name" placeholder="Course name" value={customName} onChange={(e) => setCustomName(e.target.value)} className="h-14 rounded-xl text-base" />
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-[#455561] mb-1 block">Par</label><Input type="number" value={customPar} onChange={(e) => setCustomPar(e.target.value)} className="h-12 rounded-xl text-center" /></div>
              <div><label className="text-xs text-[#455561] mb-1 block">Course Rating</label><Input type="number" step="0.1" value={customCR} onChange={(e) => setCustomCR(e.target.value)} className="h-12 rounded-xl text-center" /></div>
              <div><label className="text-xs text-[#455561] mb-1 block">Slope</label><Input type="number" value={customSlope} onChange={(e) => setCustomSlope(e.target.value)} className="h-12 rounded-xl text-center" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCustom} disabled={saving || !customName.trim()}
              className="w-full bg-[#1B4332] hover:bg-[#163B2B] text-white h-14 rounded-xl text-base font-semibold" data-testid="save-custom-course-btn">
              {saving ? 'Saving...' : 'Save Course'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
